import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AddCustomerModal } from "./AddCustomerModal";
import { CustomerList } from "./CustomerList";
import { CustomerProfileDrawer } from "./CustomerProfileDrawer";
import { RemindersList } from "./RemindersList";
import { SalesPipelineKanban } from "./SalesPipelineKanban";

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(", ") || fallback;
  return fallback;
};

export const CrmWorkspace = ({ view, authAxios }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("all");
  const [tag, setTag] = useState("all");
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authAxios().get("/admin/crm/customers");
      setCustomers(response.data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load CRM customers"));
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const openCustomer = async (id) => {
    try {
      const response = await authAxios().get(`/admin/crm/customers/${id}`);
      setSelected(response.data);
    } catch { toast.error("Could not load customer profile"); }
  };

  const mergeCustomer = (updated) => {
    setCustomers((current) => current.map((customer) => customer.id === updated.id ? { ...customer, ...updated, bookings: [] } : customer));
    setSelected((current) => current?.id === updated.id ? updated : current);
  };

  const updateCustomer = async (id, changes) => {
    try {
      const response = await authAxios().patch(`/admin/crm/customers/${id}`, changes);
      mergeCustomer(response.data);
      toast.success("Customer updated");
      return true;
    } catch (error) {
      toast.error(errorMessage(error, "Customer update failed"));
      return false;
    }
  };

  const addNote = async (id, text) => {
    try {
      const response = await authAxios().post(`/admin/crm/customers/${id}/notes`, { text });
      mergeCustomer(response.data);
      toast.success("Note saved");
      return true;
    } catch { toast.error("Could not save note"); return false; }
  };

  const createCustomer = async (payload) => {
    try {
      const response = await authAxios().post("/admin/crm/customers", payload);
      setCustomers((current) => [response.data, ...current]);
      toast.success("Customer added to CRM");
      return true;
    } catch (error) {
      toast.error(errorMessage(error, "Could not add customer"));
      return false;
    }
  };

  const completeReminder = async (id) => {
    try {
      const response = await authAxios().post(`/admin/crm/customers/${id}/follow-up/complete`);
      setCustomers((current) => current.map((customer) => customer.id === id ? { ...customer, ...response.data } : customer));
      if (selected?.id === id) setSelected(response.data);
      toast.success("Follow-up completed");
    } catch { toast.error("Could not complete reminder"); }
  };

  return <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-10">
    {view === "customers" && <CustomerList customers={customers} loading={loading} search={search} setSearch={setSearch} stage={stage} setStage={setStage} tag={tag} setTag={setTag} onSelect={openCustomer} onAdd={() => setAddOpen(true)} />}
    {view === "pipeline" && <SalesPipelineKanban customers={customers} onSelect={openCustomer} onStageChange={updateCustomer} />}
    {view === "reminders" && <RemindersList customers={customers} onSelect={openCustomer} onComplete={completeReminder} />}
    <AddCustomerModal open={addOpen} onOpenChange={setAddOpen} onCreate={createCustomer} />
    <CustomerProfileDrawer customer={selected} open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)} onUpdate={updateCustomer} onAddNote={addNote} />
  </div>;
};
